/**
 * Who changed what, and when.
 *
 * It replaced a "Reset working models" button. The button answered the wrong question: the risk on
 * this tab is not that someone cannot get back to the seed, it is that a figure moved and nobody
 * knows who moved it — the team table, the effort model and the capacity assumptions are all
 * editable, and every KPI above is computed from them.
 *
 * ⚠️ **It can only see this browser.** The state lives in `localStorage`, so the log records edits
 * made here by whoever is signed in here, and `by` is therefore always the reader themselves. It
 * becomes a real audit trail the moment the state moves server-side (open decision 3), with no UI
 * work needed — which is why it is built now. The section says this on screen rather than implying
 * a shared history that does not exist yet: a log that looks team-wide and is not would be worse
 * than no log at all.
 */
import {
  BASELINE_VERSION,
  CHANGE_LOG_LIMIT,
  type ChangeLogEntry,
  type PrioritisationState,
} from '../../../types/rolePrioritisation';
import { deviationsFromSeed } from '../../../utils/rolePrioritisationState';
import { Fold } from './CollapsedSection';

export function ChangeLog({ state }: { state: PrioritisationState }) {
  const log = state.log ?? [];
  const last = log[0];

  return (
    <Fold
      value="log"
      title="Change log"
      hint={last ? `Last: ${who(last.by)} · ${when(last.at)}` : 'No changes yet'}
    >
      {log.length ? (
        <>
          <ol className="overflow-hidden rounded-lg shadow-[inset_0_0_0_1px_#E9EAEC]">
            {log.map((e, i) => (
              <li
                key={`${e.at}-${i}`}
                className="grid grid-cols-[auto_1fr] items-baseline gap-x-3.5 gap-y-0.5 border-b border-solid border-pearl px-3.5 py-2 text-[13px] leading-5 last:border-b-0 sm:grid-cols-[132px_1fr_auto]"
              >
                <span className="tabular-nums text-iron">{when(e.at)}</span>
                <span className="text-black">{e.what}</span>
                <span className="text-steel">{who(e.by)}</span>
              </li>
            ))}
          </ol>
          <p className="mt-3 text-xs leading-4 text-iron">
            Newest first, the last {CHANGE_LOG_LIMIT} kept. Repeated edits to the same field within
            five minutes count as one. Committing, reopening and clearing the support plan are
            recorded here too; the capacity what-ifs in section 03 are not — they are scratchpad
            state, and every one of them is already visible on the cards.{' '}
            <b className="text-black">This log covers this browser only</b> — the tab&apos;s state is
            not shared yet, so it cannot show a colleague&apos;s edits.
          </p>
        </>
      ) : (
        <Empty state={state} />
      )}
    </Fold>
  );
}

/**
 * An empty log is not evidence that nothing changed — a scratchpad stored before the log existed
 * carries edited values and no history — so the values are compared against the baseline rather
 * than inferred from the log being empty.
 */
function Empty({ state }: { state: PrioritisationState }) {
  const off = deviationsFromSeed(state);
  if (!off.length) {
    return (
      <p className="text-sm leading-5 text-asphalt">
        No changes recorded, and nothing differs from baseline v{BASELINE_VERSION}. Edits made in
        this browser will be listed here with who made them.
      </p>
    );
  }
  return (
    <p className="text-sm leading-5 text-asphalt">
      No changes are recorded in this browser, but the {list(off)}{' '}
      {off.length > 1 ? 'differ' : 'differs'} from baseline v{BASELINE_VERSION} — those edits were
      made before this log existed, or in another browser. Anything changed from here on is listed
      with who changed it.
    </p>
  );
}

const list = (xs: string[]) =>
  xs.length < 2 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;

/** The most recent entry, for the one line that belongs next to the thing being edited. */
export function LastChange({ state }: { state: PrioritisationState }) {
  const last = (state.log ?? [])[0];
  if (!last) return null;
  return (
    <p className="text-xs leading-4 text-iron">
      <b className="text-black">Last change:</b> {last.what} · {who(last.by)} · {when(last.at)}
    </p>
  );
}

/** The local part is enough on an internal tool, and a full column of @finn.com reads as noise. */
function who(by: string): string {
  return by.includes('@') ? by.split('@')[0] : by;
}

function when(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return at;
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export type { ChangeLogEntry };
